import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// My Resources — curated hub of links + tools per business unit. Every
// team member has a place to find their OneDrive folders, Teams chats,
// company apps, SOP docs, and internal tools without hunting through
// emails. Admins and managers curate; workers consume.
//
// Layout: a vertical stack of category cards (Documents, Chats, Apps,
// SOPs, Tools, plus an "Other" catch-all for any custom category the
// admin added). Each card lists its resources as tiles with an icon +
// title + description and an Open ↗ link. Admin/manager curators get
// an inline edit + delete control on each tile + a "+ Add" button per
// card.
import { notify } from "../components/Toast";
import { Fragment, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import CrmBoard from "../components/crm/CrmBoard";
import KeyContactsBoard from "../components/contacts/KeyContactsBoard";
import ReplyTrackerBoard from "../components/replytracker/ReplyTrackerBoard";
import NowResourcesCard from "../components/NowResourcesCard";
import ResourceSearchPalette from "../components/ResourceSearchPalette";
import { useAuth } from "../hooks/useAuth";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
import { safeHref, sanitizeEmbedHtml } from "../lib/safeUrl";
// Fire-and-forget open logging — feeds behavior-based pin suggestions.
// Never blocks or breaks the actual open; failures are swallowed.
function logResourceOpen(resourceId) {
    void api.post(`/resources/${resourceId}/open`).catch(() => { });
}
// Stable display order + label + emoji for the well-known categories.
// Anything else falls through to the "Other" bucket at the bottom.
const KNOWN_CATEGORIES = [
    {
        key: "documents",
        label: "Documents",
        icon: "📁",
        hint: "OneDrive folders, shared drives, doc libraries — where to put + find files.",
    },
    {
        key: "chats",
        label: "Team Chats",
        icon: "💬",
        hint: "Teams channels, group chats, customer-facing rooms.",
    },
    {
        key: "applications",
        label: "Company Applications",
        icon: "🧰",
        hint: "Logins for vendor apps and internal tools you sign into every day.",
    },
    {
        key: "calendars",
        label: "Calendars",
        icon: "📅",
        hint: "Team calendars, conference rooms, scheduling links, time-off trackers.",
    },
    {
        key: "contacts",
        label: "Key Contacts",
        icon: "👤",
        hint: "Vendor reps, partner contacts, escalation lines, internal experts.",
    },
    {
        key: "sops",
        label: "SOPs & Playbooks",
        icon: "📖",
        hint: "Standard operating procedures, runbooks, decision trees.",
    },
    {
        key: "tools",
        label: "Company Tools",
        icon: "🛠️",
        hint: "CRM, Key Contacts, Reply Tracker, and embedded helpers.",
    },
];
export default function ResourcesPage() {
    const { user } = useAuth();
    // canCurate gates manager/admin-only surfaces that owners don't get
    // (onboarding paths, link health). Resource + category curation use the
    // narrower flags below.
    const canCurate = user?.role === "admin" || user?.role === "manager";
    // Adding/editing links is open to owners too (BU-scoped on the backend).
    const canManageResources = canCurate || user?.role === "owner";
    // Categories are owner/admin territory — managers can add links but not
    // shape the category set.
    const canManageCategories = user?.role === "admin" || user?.role === "owner";
    const [resources, setResources] = useState([]);
    const { businessUnits: units } = useOrgCatalog();
    // Pinned categories — keep an (otherwise empty) category card visible
    // so curators can build a category out before adding resources to it.
    const [pins, setPins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // editing = null → no modal; editing = "new" → create modal;
    // editing = Resource → edit modal seeded from that row. Kept around
    // as the fallback path — e.g. from the search palette — but the
    // category-card add/edit affordances now use the inline state below
    // instead.
    const [editing, setEditing] = useState(null);
    const [newCategoryDefault, setNewCategoryDefault] = useState("documents");
    // Phase 2 inline flows. inlineAddingInCategory = key of the category
    // card whose "+ Add" button was clicked; renders the editor expanded
    // at the top of that card's tile list. inlineEditingId = id of the
    // resource whose Edit affordance was clicked; renders the editor in
    // place of that tile. Only one can be active at a time; opening
    // either clears the other so the user can't get into a confusing
    // multi-editor state.
    const [inlineAddingInCategory, setInlineAddingInCategory] = useState(null);
    const [inlineEditingId, setInlineEditingId] = useState(null);
    // Phase 2 commit 3 — sidebar-driven category navigation. The
    // selected key drives which category card is rendered in the main
    // panel; null means "auto-pick the first visible category". The
    // sidebar lists every visible category (same visibility rules as
    // the old grid) so the user can switch between them in one click.
    // Persisted so reloads keep the user on the category they were
    // browsing, but only when the key is still visible under the
    // active BU tab (handled in activeCatKey derivation below).
    const [selectedCategoryKey, setSelectedCategoryKey] = useState(() => {
        try {
            return localStorage.getItem("resources-cat") ?? null;
        }
        catch {
            return null;
        }
    });
    function pickCategory(key) {
        setSelectedCategoryKey(key);
        try {
            localStorage.setItem("resources-cat", key);
        }
        catch {
            /* ignore quota */
        }
    }
    // Open the CRM from its Quick-access pin: bring the Tools category into
    // view (so the board mounts) and ask it to open full screen — via a
    // localStorage intent (read on mount) + a live event (if already mounted).
    function openCrm() {
        pickCategory("tools");
        pickToolsTab("crm");
        try {
            localStorage.setItem("crm-open-intent", "fullscreen");
        }
        catch {
            /* ignore */
        }
        window.dispatchEvent(new Event("crm:open-fullscreen"));
    }
    // Open the Key Contacts directory: bring Company Tools into view, flip to the
    // Key Contacts tab (so the board mounts), and ask it to expand — via a
    // localStorage intent (read on mount) + a live event (if mounted).
    function openKeyContacts() {
        pickCategory("tools");
        pickToolsTab("contacts");
        try {
            localStorage.setItem("kc-open-intent", "1");
        }
        catch {
            /* ignore */
        }
        window.dispatchEvent(new Event("kc:open"));
    }
    // Open the Reply Tracker: bring Company Tools into view + flip to its tab
    // (the board mounts already-expanded by default).
    function openReplyTracker() {
        pickCategory("tools");
        pickToolsTab("reply-tracker");
    }
    function startInlineAdd(catKey) {
        setInlineEditingId(null);
        setInlineAddingInCategory(catKey);
        // Selecting the matching category in the sidebar so the user
        // sees the form expand in the visible main panel (not on a
        // category they haven't navigated to).
        setSelectedCategoryKey(catKey);
    }
    function startInlineEdit(r) {
        setInlineAddingInCategory(null);
        setInlineEditingId(r.id);
    }
    function cancelInline() {
        setInlineAddingInCategory(null);
        setInlineEditingId(null);
    }
    // Whether the "Add category" modal is open (curators only).
    const [addingCategory, setAddingCategory] = useState(false);
    // Which category card's ⚙️ settings menu is open (Add resource / Edit /
    // Remove), keyed by cat.key. Replaces the always-on header text links.
    // catMenuPos anchors the menu fixed to the ⚙️ button so the card's overflow
    // clipping can't hide it.
    const [openCatMenu, setOpenCatMenu] = useState(null);
    const [catMenuPos, setCatMenuPos] = useState(null);
    function toggleCatMenu(key, e) {
        if (openCatMenu === key) {
            setOpenCatMenu(null);
            return;
        }
        const r = e.currentTarget.getBoundingClientRect();
        setCatMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
        setOpenCatMenu(key);
    }
    // Whether the top toolbar "+ Add ▾" menu (New resource / New category) is
    // open.
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    // Inline category-rename state. Holds the cat.key being renamed; the
    // header swaps to an input. Save POSTs the pin with the new label
    // + description. Both editable in the same expanded row so the
    // description doesn't get lost while admin focuses on the title.
    const [renamingCategory, setRenamingCategory] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [renameDescription, setRenameDescription] = useState("");
    // Emoji override for the category card heading. Pre-filled with the
    // current icon so admins can tweak instead of retyping. Empty saves
    // as null → falls back to the built-in / default icon.
    const [renameIcon, setRenameIcon] = useState("");
    const [renameSaving, setRenameSaving] = useState(false);
    // BU filter — when set, only that BU's resources (plus company-wide
    // null-BU ones) appear in the widgets. Default "" = show all BUs the
    // user has access to. Persisted so the next visit lands on the same
    // unit. Lets a user in multiple BUs scope the page to one BU at a time.
    const [activeBu, setActiveBu] = useState(() => {
        try {
            return localStorage.getItem("resources-bu") ?? "";
        }
        catch {
            return "";
        }
    });
    function pickBu(id) {
        setActiveBu(id);
        try {
            localStorage.setItem("resources-bu", id);
        }
        catch {
            /* ignore quota */
        }
    }
    // Quick access pins — caller's per-user pinned resources, drives
    // the strip at the top of the page + the Pinned group in the
    // search palette. Loaded alongside the rest of the page data.
    const [resourcePins, setResourcePins] = useState([]);
    // Behavior-based pin suggestions — resources the caller opens often but
    // hasn't pinned. Drives the "Suggested for you" row above Quick access.
    const [suggestions, setSuggestions] = useState([]);
    // CRM tracker (under Company Tools). Whether it's visible to this user
    // (BU-restricted by admins) and whether they pinned it to Quick access.
    const [crmVisible, setCrmVisible] = useState(true);
    const [crmPinned, setCrmPinned] = useState(() => {
        try {
            return localStorage.getItem("crm-pinned") === "1";
        }
        catch {
            return false;
        }
    });
    // Key Contacts + Reply Tracker are pinnable to Quick access too (same
    // pattern as the CRM): each board writes its localStorage flag + fires a
    // pin-changed event the strip below listens for.
    const [kcPinned, setKcPinned] = useState(() => {
        try {
            return localStorage.getItem("kc-pinned") === "1";
        }
        catch {
            return false;
        }
    });
    const [rtPinned, setRtPinned] = useState(() => {
        try {
            return localStorage.getItem("reply-tracker-pinned") === "1";
        }
        catch {
            return false;
        }
    });
    // Global search palette (⌘K). Opening it focuses the input.
    const [searchOpen, setSearchOpen] = useState(false);
    const [toolsTab, setToolsTab] = useState(() => {
        try {
            const saved = localStorage.getItem("resources-tools-tab");
            if (saved === "contacts" || saved === "reply-tracker")
                return saved;
            return "crm";
        }
        catch {
            return "crm";
        }
    });
    function pickToolsTab(t) {
        setToolsTab(t);
        try {
            localStorage.setItem("resources-tools-tab", t);
        }
        catch {
            /* ignore */
        }
    }
    async function load() {
        setLoading(true);
        setError(null);
        try {
            // Everyone (curator or not) needs the units list — workers use it
            // to pick a BU filter when they belong to more than one. The
            // /orgs/business-units endpoint is read-open per the org spec.
            // Category pins ride along so empty pinned cards render too.
            const [r, p, rp, sg, cv] = await Promise.all([
                api.get("/resources"),
                api.get("/resources/category-pins"),
                api.get("/resources/pins"),
                // Settle independently — if the suggestions endpoint isn't
                // deployed yet, the rest of the page still loads.
                api
                    .get("/resources/pin-suggestions")
                    .catch(() => []),
                api
                    .get("/crm/visibility")
                    .catch(() => null),
            ]);
            setResources(r);
            setPins(p);
            setResourcePins(rp);
            setSuggestions(sg);
            setCrmVisible(cv ? cv.visible_to_me : true);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load resources");
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);
    // ⌘K / Ctrl+K opens the global search palette from anywhere on the
    // page. Listen once on document so the shortcut works even when no
    // input is focused. Skip when the user is already typing in another
    // input — they're searching that, not us.
    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                const target = e.target;
                const tag = target?.tagName?.toLowerCase();
                if (tag === "input" || tag === "textarea")
                    return;
                e.preventDefault();
                setSearchOpen(true);
            }
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);
    // Keep the Quick-access CRM tile in sync when it's pinned/unpinned from
    // the CRM board (which dispatches this event after writing localStorage).
    useEffect(() => {
        const sync = () => {
            try {
                setCrmPinned(localStorage.getItem("crm-pinned") === "1");
                setKcPinned(localStorage.getItem("kc-pinned") === "1");
                setRtPinned(localStorage.getItem("reply-tracker-pinned") === "1");
            }
            catch {
                /* ignore */
            }
        };
        window.addEventListener("crm:pin-changed", sync);
        window.addEventListener("kc:pin-changed", sync);
        window.addEventListener("reply-tracker:pin-changed", sync);
        return () => {
            window.removeEventListener("crm:pin-changed", sync);
            window.removeEventListener("kc:pin-changed", sync);
            window.removeEventListener("reply-tracker:pin-changed", sync);
        };
    }, []);
    // Set of pinned resource ids — drives the Quick access strip + the
    // palette's "Quick access" group. Looked up O(1) per render.
    const pinnedIds = useMemo(() => new Set(resourcePins.map((p) => p.resource_id)), [resourcePins]);
    // Pin objects sorted by order_index, joined with the resource row
    // so the strip can render a real tile. Resources the caller can no
    // longer see (deleted, or BU access revoked) are silently dropped.
    const pinnedTiles = useMemo(() => {
        const byId = new Map(resources.map((r) => [r.id, r]));
        return resourcePins
            .slice()
            .sort((a, b) => a.order_index - b.order_index)
            .map((p) => ({
            pin: p,
            resource: byId.get(p.resource_id) ?? null,
        }))
            .filter((x) => x.resource !== null);
    }, [resourcePins, resources]);
    // Toggle a resource in/out of the Quick access strip. Optimistic —
    // updates local state immediately, then commits to the server. We
    // re-fetch /resources/pins after every successful write so the UI
    // shows the authoritative server state (don't trust a single mutation
    // to be enough — pins that "didn't stay saved" were almost certainly
    // a stale local state that diverged from what the DB actually held).
    async function togglePin(resource) {
        // Guard: only one toggle in flight at a time per resource to avoid
        // the optimistic-vs-POST race where the user clicks Pin twice and
        // the second click can't find the (still-optimistic) row to delete.
        const existing = resourcePins.find((p) => p.resource_id === resource.id);
        if (existing) {
            // Unpin — optimistic remove, then DELETE. If the row is still
            // optimistic (no real id yet), skip the DELETE and just refetch:
            // a "delete optimistic-XYZ" call would 404 and reloading is what
            // we want anyway.
            setResourcePins((prev) => prev.filter((p) => p.id !== existing.id));
            const isOptimistic = String(existing.id).startsWith("optimistic-");
            try {
                if (!isOptimistic) {
                    await api.del(`/resources/pins/${existing.id}`);
                }
            }
            catch {
                /* fall through to reload */
            }
            // Always reconcile with server so the strip matches the DB.
            try {
                const rp = await api.get("/resources/pins");
                setResourcePins(rp);
            }
            catch {
                /* leave optimistic state in place — next page load will fix it */
            }
            return;
        }
        // Pin — optimistic prepend at the tail, then POST.
        const optimistic = {
            id: `optimistic-${resource.id}`,
            resource_id: resource.id,
            order_index: resourcePins.length,
        };
        setResourcePins((prev) => [...prev, optimistic]);
        try {
            await api.post("/resources/pins", {
                resource_id: resource.id,
            });
        }
        catch {
            /* fall through to reload — the catch reloads the strip from server */
        }
        // Re-fetch the strip from the server so we know the pin really
        // persisted. This is the difference between "pinned in my head"
        // and "pinned in the DB" — only the second one survives a reload.
        try {
            const rp = await api.get("/resources/pins");
            setResourcePins(rp);
        }
        catch {
            /* leave optimistic state in place — next page load will fix it */
        }
    }
    // Accept a suggestion → pin it and drop it from the Suggested row.
    function pinSuggestion(s) {
        setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
        void togglePin(s);
    }
    // Wave a suggestion off — snoozed server-side for ~14 days. Optimistic
    // remove; a failure just means it reappears on the next load.
    function dismissSuggestion(s) {
        setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
        void api
            .post(`/resources/pin-suggestions/${s.id}/dismiss`)
            .catch(() => { });
    }
    // Apply the BU filter. A resource is visible when its BU set contains
    // the active BU (multi-BU model: a resource tagged with Marketing +
    // Sales shows under both filters). No more company-wide null bypass.
    const visibleResources = useMemo(() => {
        if (!activeBu)
            return resources;
        return resources.filter((r) => r.business_unit_ids.includes(activeBu));
    }, [resources, activeBu]);
    // Which BUs the caller actually has access to. Admins/managers
    // see everything; everyone else is limited to the BUs they're a
    // member of. Used to lock the BU tabs the user can't open and to
    // render an access-denied panel instead of the resource list when
    // they click one anyway. We show all BUs as tabs (rather than
    // hiding the ones the user can't see) so they understand the
    // company's full team structure and have a clear path to request
    // access.
    const accessibleBuIds = useMemo(() => {
        const ids = new Set();
        if (user?.role === "admin" || user?.role === "manager") {
            for (const u of units)
                ids.add(u.id);
            return ids;
        }
        // Worker access — the backend already filters /resources to BUs
        // this user can see, so any BU appearing on their resource list
        // is a BU they belong to. The primary business_unit_id covers
        // the edge case of a brand-new user with no resources yet.
        for (const r of resources) {
            for (const bid of r.business_unit_ids)
                ids.add(bid);
        }
        if (user?.business_unit_id)
            ids.add(user.business_unit_id);
        return ids;
    }, [user, units, resources]);
    const activeBuIsLocked = activeBu !== "" && !accessibleBuIds.has(activeBu);
    const activeBuName = units.find((u) => u.id === activeBu)?.name;
    // Bucket the (filtered) resources by their category. Unknown
    // categories collapse into a synthetic "Other" group so freeform
    // admin-coined categories still find a home.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const r of visibleResources) {
            const k = r.category;
            if (!map.has(k))
                map.set(k, []);
            map.get(k).push(r);
        }
        return map;
    }, [visibleResources]);
    // category name → ALL its pins (company-wide + every BU-scoped one).
    // We make the visibility decision ourselves in categoryVisible below,
    // so this is keyed off the full pin list, not a pre-filtered slice.
    const pinsByCategory = useMemo(() => {
        const m = new Map();
        for (const p of pins) {
            if (!m.has(p.name))
                m.set(p.name, []);
            m.get(p.name).push(p);
        }
        return m;
    }, [pins]);
    // A category widget is "BU-assigned" when it has at least one
    // BU-scoped pin. In the "All" view those are hidden from anyone who
    // isn't a member of one of their BUs — they can still reach them via
    // that BU's filter chip. Company-wide / unpinned categories — and
    // built-in categories with no pin — always show. Built-ins become
    // BU-assigned the moment they get a BU-scoped pin.
    function categoryVisible(catKey) {
        const catPins = pinsByCategory.get(catKey) ?? [];
        const buScoped = catPins.filter((p) => p.business_unit_id !== null);
        if (buScoped.length === 0)
            return true; // company-wide / unpinned
        if (activeBu === "") {
            return buScoped.some((p) => p.viewer_is_member);
        }
        return buScoped.some((p) => p.business_unit_id === activeBu);
    }
    // Custom (non-built-in) category keys that could need a card: any
    // category a visible resource uses, plus any pinned category name.
    // Final per-view visibility is decided by categoryVisible() in render.
    const customKeys = useMemo(() => {
        const keys = new Set();
        const isKnown = (k) => KNOWN_CATEGORIES.some((c) => c.key === k);
        for (const k of grouped.keys())
            if (!isKnown(k))
                keys.add(k);
        for (const name of pinsByCategory.keys())
            if (!isKnown(name))
                keys.add(name);
        return Array.from(keys).sort((a, b) => a.localeCompare(b));
    }, [grouped, pinsByCategory]);
    async function handleDelete(r) {
        if (!confirm(`Remove "${r.title}" from the resources hub?`))
            return;
        try {
            await api.del(`/resources/${r.id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete");
        }
    }
    // Remove an empty pinned category card. Deletes every pin for that
    // category name the caller is allowed to remove (usually just one).
    // Resources are never touched — a category with resources still shows
    // because of those resources, not the pin.
    async function handleRemoveCategory(name, catPins) {
        const removable = catPins.filter((p) => p.can_edit);
        if (removable.length === 0)
            return;
        if (!confirm(`Remove the "${name}" category card? Resources aren't affected.`))
            return;
        try {
            for (const p of removable) {
                await api.del(`/resources/category-pins/${p.id}`);
            }
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't remove category");
        }
    }
    // Open the inline rename input on a category card. Seeds label,
    // description AND icon from whatever's currently showing so the admin
    // just tweaks instead of retyping.
    function startRename(catKey, currentLabel, currentDescription, currentIcon) {
        setRenamingCategory(catKey);
        setRenameValue(currentLabel);
        setRenameDescription(currentDescription);
        setRenameIcon(currentIcon);
    }
    // Persist a renamed category. Strategy:
    //   - If a pin already exists for this (name, BU) the user can edit,
    //     PATCH its label.
    //   - Otherwise POST a new pin with name=key + label=newName. The
    //     POST endpoint is idempotent and accepts label, so it doubles
    //     as both create-and-rename and edit-label.
    //   - Empty new label clears the override by PATCHing label=null
    //     on the existing pin (or no-op if no pin exists).
    // BU scope: if the user has an activeBu filter, scope the pin there;
    // otherwise create it company-wide (NULL) so the rename applies to
    // every viewer of that category.
    async function saveRename(catKey, catPins) {
        const newLabel = renameValue.trim();
        const newDescription = renameDescription.trim();
        const newIcon = renameIcon.trim();
        setRenameSaving(true);
        try {
            const editablePin = catPins.find((p) => p.can_edit);
            if (editablePin) {
                await api.patch(`/resources/category-pins/${editablePin.id}`, {
                    label: newLabel || null,
                    description: newDescription || null,
                    icon: newIcon || null,
                });
            }
            else if (newLabel || newDescription || newIcon) {
                await api.post("/resources/category-pins", {
                    name: catKey,
                    label: newLabel || null,
                    description: newDescription || null,
                    icon: newIcon || null,
                    business_unit_id: activeBu || null,
                });
            }
            setRenamingCategory(null);
            setRenameValue("");
            setRenameDescription("");
            setRenameIcon("");
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't rename category");
        }
        finally {
            setRenameSaving(false);
        }
    }
    if (!user)
        return null;
    // Show the BU-filter chips only when the user actually has access
    // to more than one BU (or is a curator who might want to slice by
    // unit). For workers in a single BU, the page just shows that BU's
    // resources by default.
    return (_jsxs("div", { className: "page resources-page", children: [_jsxs("header", { className: "resources-head", children: [_jsxs("div", { children: [_jsx("h1", { children: "My Resources" }), _jsx("p", { className: "muted small", children: "One place for everything your team uses every day \u2014 file locations, chats, apps, SOPs, and quick tools. Pick a business unit to see its tools, or stay on All to see everything you have access to." })] }), (canManageResources || canManageCategories) && (_jsxs("div", { className: "resources-add-wrap", children: [_jsx("button", { type: "button", className: "btn-primary resources-add-category", onClick: () => setAddMenuOpen((o) => !o), "aria-haspopup": "menu", "aria-expanded": addMenuOpen, title: "Add a resource or a category", children: "+ Add \u25BE" }), addMenuOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: "resources-menu-backdrop", onClick: () => setAddMenuOpen(false) }), _jsxs("div", { className: "resources-dropdown resources-add-menu", role: "menu", children: [canManageResources && (_jsxs("button", { type: "button", role: "menuitem", onClick: () => {
                                                    setAddMenuOpen(false);
                                                    setEditing("new");
                                                }, children: ["\uD83D\uDD17 New resource", _jsx("span", { className: "resources-add-menu-hint", children: "A link, doc, or embedded widget" })] })), canManageCategories && (_jsxs("button", { type: "button", role: "menuitem", onClick: () => {
                                                    setAddMenuOpen(false);
                                                    setAddingCategory(true);
                                                }, children: ["\uD83D\uDDC2\uFE0F New category", _jsx("span", { className: "resources-add-menu-hint", children: "A new group to file resources under" })] }))] })] }))] }))] }), _jsx(NowResourcesCard, {}), _jsxs("button", { type: "button", className: "resources-search-trigger", onClick: () => setSearchOpen(true), "aria-label": "Search resources (\u2318K)", children: [_jsx("span", { className: "resources-search-trigger-icon", "aria-hidden": "true", children: "\uD83D\uDD0D" }), _jsx("span", { className: "resources-search-trigger-placeholder", children: "Search by name, description, URL, or category\u2026" }), _jsx("kbd", { className: "resources-search-kbd", children: "\u2318K" })] }), suggestions.length > 0 && (_jsxs("div", { className: "resources-suggested", children: [_jsxs("div", { className: "resources-suggested-head", children: [_jsx("span", { className: "resources-suggested-label", children: "\u2728 Suggested for you" }), _jsx("span", { className: "muted small", children: "\u00B7 based on what you open most" })] }), _jsx("div", { className: "resources-suggested-grid", children: suggestions.map((s) => (_jsxs("div", { className: "resources-suggested-tile", children: [_jsxs("a", { className: "resources-suggested-body", href: safeHref(s.url), target: "_blank", rel: "noopener noreferrer", onClick: () => logResourceOpen(s.id), title: s.url, children: [_jsx("span", { className: "resources-suggested-icon", "aria-hidden": "true", children: s.icon || "🔗" }), _jsxs("span", { className: "resources-suggested-text", children: [_jsx("strong", { children: s.title }), _jsxs("span", { className: "muted small", children: ["Opened ", s.recent_open_count, " times this week"] })] })] }), _jsxs("div", { className: "resources-suggested-actions", children: [_jsx("button", { type: "button", className: "resources-suggested-pin", onClick: () => pinSuggestion(s), title: "Pin to Quick access", children: "\uD83D\uDCCC Pin" }), _jsx("button", { type: "button", className: "resources-suggested-dismiss", onClick: () => dismissSuggestion(s), title: "Not useful \u2014 hide this for a couple weeks", "aria-label": "Dismiss suggestion", children: "\u00D7" })] })] }, s.id))) })] })), (pinnedTiles.length > 0 ||
                resources.length > 0 ||
                (crmPinned && crmVisible) ||
                kcPinned ||
                rtPinned) && (_jsxs("div", { className: "resources-quickaccess", children: [_jsxs("div", { className: "resources-quickaccess-head", children: [_jsx("span", { className: "resources-quickaccess-label", children: "\uD83D\uDCCC Quick access" }), pinnedTiles.length > 0 && (_jsxs("span", { className: "muted small", children: ["\u00B7 ", pinnedTiles.length, " ", pinnedTiles.length === 1 ? "pin" : "pins"] }))] }), _jsxs("div", { className: "resources-quickaccess-grid", children: [crmPinned && crmVisible && (_jsxs("div", { className: "resources-quickaccess-tile", title: "Open the Lead Pipeline (CRM)", children: [_jsxs("button", { type: "button", className: "resources-quickaccess-tile-body", onClick: openCrm, children: [_jsx("span", { className: "resources-quickaccess-tile-icon", "aria-hidden": "true", children: "\uD83C\uDFAF" }), _jsx("span", { className: "resources-quickaccess-tile-text", children: _jsx("strong", { children: "Lead Pipeline (CRM)" }) })] }), _jsx("button", { type: "button", className: "resources-quickaccess-unpin", onClick: () => {
                                            try {
                                                localStorage.setItem("crm-pinned", "0");
                                            }
                                            catch {
                                                /* ignore */
                                            }
                                            setCrmPinned(false);
                                            window.dispatchEvent(new Event("crm:pin-changed"));
                                        }, title: "Unpin from Quick access", "aria-label": "Unpin CRM", children: "\u00D7" })] })), kcPinned && (_jsxs("div", { className: "resources-quickaccess-tile", title: "Open the Key Contacts directory", children: [_jsxs("button", { type: "button", className: "resources-quickaccess-tile-body", onClick: openKeyContacts, children: [_jsx("span", { className: "resources-quickaccess-tile-icon", "aria-hidden": "true", children: "\uD83D\uDC64" }), _jsx("span", { className: "resources-quickaccess-tile-text", children: _jsx("strong", { children: "Key Contacts" }) })] }), _jsx("button", { type: "button", className: "resources-quickaccess-unpin", onClick: () => {
                                            try {
                                                localStorage.setItem("kc-pinned", "0");
                                            }
                                            catch {
                                                /* ignore */
                                            }
                                            setKcPinned(false);
                                            window.dispatchEvent(new Event("kc:pin-changed"));
                                        }, title: "Unpin from Quick access", "aria-label": "Unpin Key Contacts", children: "\u00D7" })] })), rtPinned && (_jsxs("div", { className: "resources-quickaccess-tile", title: "Open the Reply Tracker", children: [_jsxs("button", { type: "button", className: "resources-quickaccess-tile-body", onClick: openReplyTracker, children: [_jsx("span", { className: "resources-quickaccess-tile-icon", "aria-hidden": "true", children: "\uD83D\uDCE8" }), _jsx("span", { className: "resources-quickaccess-tile-text", children: _jsx("strong", { children: "Reply Tracker" }) })] }), _jsx("button", { type: "button", className: "resources-quickaccess-unpin", onClick: () => {
                                            try {
                                                localStorage.setItem("reply-tracker-pinned", "0");
                                            }
                                            catch {
                                                /* ignore */
                                            }
                                            setRtPinned(false);
                                            window.dispatchEvent(new Event("reply-tracker:pin-changed"));
                                        }, title: "Unpin from Quick access", "aria-label": "Unpin Reply Tracker", children: "\u00D7" })] })), pinnedTiles.map(({ pin, resource }) => (_jsxs("div", { className: "resources-quickaccess-tile", title: resource.url, children: [_jsxs("button", { type: "button", className: "resources-quickaccess-tile-body", onClick: () => {
                                            logResourceOpen(resource.id);
                                            window.open(resource.url, "_blank", "noopener,noreferrer");
                                        }, children: [_jsx("span", { className: "resources-quickaccess-tile-icon", "aria-hidden": "true", children: resource.icon || "🔗" }), _jsx("span", { className: "resources-quickaccess-tile-text", children: _jsx("strong", { children: resource.title }) })] }), _jsx("button", { type: "button", className: "resources-quickaccess-unpin", onClick: () => togglePin(resource), title: "Unpin from Quick access", "aria-label": "Unpin", children: "\u00D7" })] }, pin.id))), _jsxs("button", { type: "button", className: "resources-quickaccess-add", onClick: () => setSearchOpen(true), title: "Open search, then tap \uD83D\uDCCC on any result to pin it", children: [_jsx("span", { "aria-hidden": "true", children: "+" }), _jsx("span", { children: "Pin a resource" })] })] })] })), units.length > 0 && (_jsxs("div", { className: "resources-bu-tabs", role: "tablist", "aria-label": "Filter by business unit", children: [_jsxs("button", { type: "button", role: "tab", "aria-selected": activeBu === "", className: `resources-bu-tab ${activeBu === "" ? "active" : ""}`, onClick: () => pickBu(""), title: "All resources across every business unit you can see", children: [_jsx("span", { children: "All teams" }), _jsx("span", { className: "resources-bu-tab-count", children: resources.length })] }), units.map((bu) => {
                        const count = resources.filter((r) => r.business_unit_ids.includes(bu.id)).length;
                        const locked = !accessibleBuIds.has(bu.id);
                        return (_jsxs("button", { type: "button", role: "tab", "aria-selected": activeBu === bu.id, className: `resources-bu-tab ${activeBu === bu.id ? "active" : ""} ${locked ? "locked" : ""}`, onClick: () => pickBu(bu.id), title: locked
                                ? `You don't have access to ${bu.name}. Click to learn more.`
                                : undefined, children: [locked && (_jsx("span", { "aria-hidden": "true", className: "resources-bu-tab-lock", children: "\uD83D\uDD12" })), _jsx("span", { children: bu.name }), !locked && (_jsx("span", { className: "resources-bu-tab-count", children: count }))] }, bu.id));
                    })] })), loading && _jsx("div", { className: "loading", children: "Loading resources\u2026" }), error && _jsx("div", { className: "error", children: error }), !loading && activeBuIsLocked && (_jsxs("section", { className: "resources-locked-card", children: [_jsx("div", { className: "resources-locked-icon", "aria-hidden": "true", children: "\uD83D\uDD12" }), _jsxs("h2", { className: "resources-locked-title", children: ["You don't have access to", " ", activeBuName ?? "this business unit"] }), _jsxs("p", { className: "resources-locked-body", children: ["Resources here are scoped to people on the", " ", _jsx("strong", { children: activeBuName ?? "selected" }), " team. If this is a mistake, ask your administrator for help \u2014 they can add you to the unit in Settings."] }), _jsx("button", { type: "button", className: "btn-link", onClick: () => pickBu(""), children: "\u2190 Back to All teams" })] })), !loading && !activeBuIsLocked && (() => {
                // Build the unified category descriptor list once. The same
                // array previously sat inline; lifting it lets both the new
                // sidebar and the main panel iterate over the same filtered
                // set without diverging.
                // Key Contacts no longer gets its own category — it lives as a tab
                // under Company Tools — so drop "contacts" from the sidebar list.
                const allCats = [
                    ...KNOWN_CATEGORIES.filter((c) => c.key !== "contacts"),
                    ...customKeys.map((k) => ({
                        key: k,
                        label: k.charAt(0).toUpperCase() + k.slice(1),
                        icon: "🔗",
                        hint: "Custom resource group.",
                    })),
                ];
                // Tools that live under Company Tools (each is +1 on the count): the
                // CRM (when visible to this user), the Key Contacts directory, and the
                // Reply Tracker.
                const toolCount = (crmVisible ? 1 : 0) + 2;
                const visibleCats = allCats.filter((cat) => {
                    if (!categoryVisible(cat.key))
                        return false;
                    // When filtering by a specific business unit, hide any category
                    // that has no resources for that team — an empty widget under a BU
                    // filter is just noise (for everyone). The "tools" hub is exempt:
                    // it hosts the CRM / Key Contacts / Reply Tracker, not resources.
                    // Empty categories still appear under "All" so curators can manage
                    // them.
                    if (activeBu !== "" && cat.key !== "tools") {
                        if ((grouped.get(cat.key) ?? []).length === 0)
                            return false;
                    }
                    return true;
                });
                // Special sentinel for the synthetic "All" entry at the top
                // of the sidebar — when active, the main panel renders every
                // visible category stacked (the old grid view, but reachable
                // from the new sidebar). Persists like any other key.
                const ALL_KEY = "__all__";
                // Active key resolution: "All" sticks (it's always valid).
                // Otherwise prefer the selected key if it's still visible
                // under the active BU tab; otherwise fall back to the first
                // visible category. This keeps the user anchored to their
                // preference but recovers gracefully if they swap BU tabs and
                // their selected category is no longer in scope.
                const activeCatKey = selectedCategoryKey === ALL_KEY
                    ? ALL_KEY
                    : (selectedCategoryKey &&
                        visibleCats.find((c) => c.key === selectedCategoryKey)?.key) ||
                        visibleCats[0]?.key ||
                        null;
                const showingAll = activeCatKey === ALL_KEY;
                return (_jsxs("div", { className: "resources-split", children: [_jsxs("aside", { className: "resources-cat-sidebar", "aria-label": "Resource categories", children: [_jsx("div", { className: "resources-cat-sidebar-h", children: "Categories" }), visibleCats.length === 0 && (_jsx("div", { className: "muted small resources-cat-sidebar-empty", children: "No categories under this team yet." })), visibleCats.length > 0 && (_jsxs("button", { type: "button", className: `resources-cat-link ${showingAll ? "active" : ""}`, onClick: () => pickCategory(ALL_KEY), children: [_jsx("span", { className: "resources-cat-link-icon", "aria-hidden": "true", children: "\uD83D\uDCDA" }), _jsx("span", { className: "resources-cat-link-label", children: "All" }), _jsx("span", { className: "resources-cat-link-count", children: visibleResources.length + toolCount })] })), visibleCats.map((cat) => {
                                    const count = (grouped.get(cat.key) ?? []).length +
                                        (cat.key === "tools" ? toolCount : 0); // tools: CRM + Key Contacts
                                    const isActive = cat.key === activeCatKey;
                                    const catPins = pinsByCategory.get(cat.key) ?? [];
                                    const overrideIcon = catPins.find((p) => p.icon)?.icon ?? null;
                                    const overrideLabel = catPins.find((p) => p.label)?.label ?? null;
                                    return (_jsxs("button", { type: "button", className: `resources-cat-link ${isActive ? "active" : ""}`, onClick: () => pickCategory(cat.key), children: [_jsx("span", { className: "resources-cat-link-icon", "aria-hidden": "true", children: overrideIcon ?? cat.icon }), _jsx("span", { className: "resources-cat-link-label", children: overrideLabel ?? cat.label }), _jsx("span", { className: "resources-cat-link-count", children: count })] }, cat.key));
                                })] }), _jsx("div", { className: "resources-cat-main", children: _jsx("div", { className: `resources-grid ${showingAll ? "two-col" : ""}`, children: visibleCats
                                    // Sidebar drives which category is rendered in the main
                                    // panel. "All" → render every visible category stacked.
                                    // Specific key → render only that one. The grid container
                                    // stays (now flexes column-major) so all the per-category
                                    // JSX below keeps working unchanged.
                                    .filter((cat) => showingAll || cat.key === activeCatKey)
                                    .map((cat) => {
                                    const rows = grouped.get(cat.key) ?? [];
                                    const catPins = pinsByCategory.get(cat.key) ?? [];
                                    // BU-scoped pins → which business unit(s) this widget is
                                    // assigned to, surfaced as small tags so the scoping stays
                                    // legible even though the grid isn't grouped by BU.
                                    const catBuNames = Array.from(new Set(catPins
                                        .filter((p) => p.business_unit_id !== null)
                                        .map((p) => p.business_unit_name)
                                        .filter((n) => !!n)));
                                    // An empty pinned category can be unpinned to hide its card.
                                    const canRemoveCategory = canManageCategories &&
                                        rows.length === 0 &&
                                        catPins.some((p) => p.can_edit);
                                    // Apply admin-set overrides if any pin carries them. First
                                    // non-null wins; falls back to the built-in / titlecased
                                    // label and the built-in hint / icon.
                                    const overrideLabel = catPins.find((p) => p.label)?.label ?? null;
                                    const overrideDescription = catPins.find((p) => p.description)?.description ?? null;
                                    const overrideIcon = catPins.find((p) => p.icon)?.icon ?? null;
                                    const displayLabel = overrideLabel ?? cat.label;
                                    const displayDescription = overrideDescription ?? cat.hint;
                                    const displayIcon = overrideIcon ?? cat.icon;
                                    const isRenaming = renamingCategory === cat.key;
                                    return (_jsx(Fragment, { children: _jsxs("section", { className: `resources-card ${cat.key === "tools" ? "resources-card-fullspan" : ""}`, children: [_jsxs("header", { className: "resources-card-head", children: [_jsx("span", { className: "resources-card-icon", "aria-hidden": "true", children: displayIcon }), _jsx("div", { className: "resources-card-titleblock", children: isRenaming ? (_jsxs("div", { className: "resources-card-rename", children: [_jsxs("div", { className: "resources-card-rename-row", children: [_jsx("input", { className: "resources-card-rename-icon", value: renameIcon, onChange: (e) => setRenameIcon(e.target.value), placeholder: cat.icon, "aria-label": "Category emoji", title: "Emoji shown at the top of this category card" }), _jsx("input", { autoFocus: true, value: renameValue, onChange: (e) => setRenameValue(e.target.value), onKeyDown: (e) => {
                                                                                    if (e.key === "Escape") {
                                                                                        setRenamingCategory(null);
                                                                                        setRenameValue("");
                                                                                        setRenameDescription("");
                                                                                        setRenameIcon("");
                                                                                    }
                                                                                }, placeholder: cat.label, maxLength: 80, "aria-label": "Category name" })] }), _jsx("textarea", { rows: 2, value: renameDescription, onChange: (e) => setRenameDescription(e.target.value), placeholder: cat.hint, className: "resources-card-rename-desc", "aria-label": "Category description" }), _jsxs("div", { className: "resources-card-rename-actions", children: [_jsx("button", { type: "button", className: "btn-link", disabled: renameSaving, onClick: () => saveRename(cat.key, catPins), children: renameSaving ? "Saving…" : "Save" }), _jsx("button", { type: "button", className: "btn-link", disabled: renameSaving, onClick: () => {
                                                                                    setRenamingCategory(null);
                                                                                    setRenameValue("");
                                                                                    setRenameDescription("");
                                                                                    setRenameIcon("");
                                                                                }, children: "Cancel" })] })] })) : (_jsxs(_Fragment, { children: [_jsx("h2", { children: displayLabel }), _jsx("p", { className: "muted small", children: displayDescription }), catBuNames.length > 0 && (_jsx("div", { className: "resources-card-bu-tags", children: catBuNames.map((n) => (_jsx("span", { className: "pill resources-card-bu-tag", title: "This category is assigned to this business unit", children: n }, n))) }))] })) }), !isRenaming &&
                                                            cat.key !== "tools" &&
                                                            (canManageResources ||
                                                                canManageCategories ||
                                                                canRemoveCategory) && (_jsxs("div", { className: "resources-card-menu-wrap", children: [_jsx("button", { type: "button", className: "resources-card-cog", onClick: (e) => toggleCatMenu(cat.key, e), "aria-haspopup": "menu", "aria-expanded": openCatMenu === cat.key, "aria-label": "Category settings", title: "Category settings", children: "\u2699\uFE0F" }), openCatMenu === cat.key && catMenuPos && (_jsxs(_Fragment, { children: [_jsx("div", { className: "resources-menu-backdrop", onClick: () => setOpenCatMenu(null) }), _jsxs("div", { className: "resources-dropdown", role: "menu", style: {
                                                                                position: "fixed",
                                                                                top: catMenuPos.top,
                                                                                right: catMenuPos.right,
                                                                            }, children: [canManageResources && (_jsx("button", { type: "button", role: "menuitem", onClick: () => {
                                                                                        setOpenCatMenu(null);
                                                                                        startInlineAdd(cat.key);
                                                                                    }, children: "\u2795 Add resource" })), canManageCategories && (_jsx("button", { type: "button", role: "menuitem", onClick: () => {
                                                                                        setOpenCatMenu(null);
                                                                                        startRename(cat.key, displayLabel, displayDescription, displayIcon);
                                                                                    }, children: "\u270F\uFE0F Edit category" })), canRemoveCategory && (_jsx("button", { type: "button", role: "menuitem", className: "danger", onClick: () => {
                                                                                        setOpenCatMenu(null);
                                                                                        handleRemoveCategory(cat.key, catPins);
                                                                                    }, children: "\uD83D\uDDD1 Remove category" }))] })] }))] }))] }), inlineAddingInCategory === cat.key && cat.key !== "tools" && (_jsx(ResourceEditor, { initial: null, defaultCategory: cat.key, units: units, canPickBu: canManageResources, inline: true, defaultBusinessUnitIds: activeBu ? [activeBu] : [], onClose: cancelInline, onSaved: async () => {
                                                        cancelInline();
                                                        await load();
                                                    } })), rows.length === 0 ? (
                                                // Company Tools is the home for the tool tabs (below), not a
                                                // resource widget — so it never shows the empty-state nudge.
                                                cat.key === "tools" ? null : (_jsxs("div", { className: "muted small resources-empty", children: ["Nothing here yet.", " ", canManageResources
                                                            ? "Click + Add to pin a resource."
                                                            : "Check back once an admin adds something."] }))) : (_jsx("ul", { className: "resources-list", children: rows.map((r) => {
                                                        // When this tile is being inline-edited, swap
                                                        // the tile for the editor in the same slot.
                                                        // Cancelling restores the tile; saving reloads
                                                        // the page data so the row reflects the edit.
                                                        if (inlineEditingId === r.id) {
                                                            return (_jsx("li", { className: "resources-list-edit-slot", children: _jsx(ResourceEditor, { initial: r, defaultCategory: r.category, units: units, canPickBu: canManageResources, inline: true, onClose: cancelInline, onSaved: async () => {
                                                                        cancelInline();
                                                                        await load();
                                                                    } }) }, r.id));
                                                        }
                                                        return (_jsx(ResourceTile, { row: r, totalBuCount: units.length, 
                                                            // Hide the BU label on the "All" filter —
                                                            // the dot-separated list of every assigned
                                                            // BU makes tiles tall and hard to scan.
                                                            // When a specific BU is active, the filter
                                                            // already disambiguates so the label adds
                                                            // little; but keep it visible there for
                                                            // now since the user only flagged All.
                                                            showBuLabel: activeBu !== "", isPinned: pinnedIds.has(r.id), onTogglePin: () => togglePin(r), onEdit: () => startInlineEdit(r), onDelete: () => handleDelete(r), onOpen: () => logResourceOpen(r.id) }, r.id));
                                                    }) })), cat.key === "tools" && (() => {
                                                    // CRM may be hidden for this user; fall back to Key Contacts.
                                                    const activeTool = !crmVisible && toolsTab === "crm" ? "contacts" : toolsTab;
                                                    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "resources-tools-tabs", role: "tablist", "aria-label": "Company tools", children: [crmVisible && (_jsx("button", { type: "button", role: "tab", "aria-selected": activeTool === "crm", className: `resources-tools-tab ${activeTool === "crm" ? "active" : ""}`, onClick: () => pickToolsTab("crm"), children: "\uD83C\uDFAF Lead Pipeline (CRM)" })), _jsx("button", { type: "button", role: "tab", "aria-selected": activeTool === "contacts", className: `resources-tools-tab ${activeTool === "contacts" ? "active" : ""}`, onClick: () => pickToolsTab("contacts"), children: "\uD83D\uDC64 Key Contacts" }), _jsx("button", { type: "button", role: "tab", "aria-selected": activeTool === "reply-tracker", className: `resources-tools-tab ${activeTool === "reply-tracker" ? "active" : ""}`, onClick: () => pickToolsTab("reply-tracker"), children: "\uD83D\uDCE8 Reply Tracker" })] }), activeTool === "crm" && crmVisible && _jsx(CrmBoard, {}), activeTool === "contacts" && _jsx(KeyContactsBoard, {}), activeTool === "reply-tracker" && _jsx(ReplyTrackerBoard, {})] }));
                                                })()] }) }, cat.key));
                                }) }) })] }));
            })(), !loading && (_jsx(OnboardingPathSection, { canCurate: canCurate, units: units })), !loading && canCurate && _jsx(LinkHealthSection, {}), editing && (_jsx(ResourceEditor, { initial: editing === "new" ? null : editing, defaultCategory: editing === "new" ? newCategoryDefault : editing.category, units: units, canPickBu: canManageResources, onClose: () => setEditing(null), onSaved: async () => {
                    setEditing(null);
                    await load();
                } })), addingCategory && (_jsx(AddCategoryModal, { units: units, defaultBu: activeBu, existingCategories: [
                    ...KNOWN_CATEGORIES.map((c) => c.key),
                    ...customKeys,
                ], onClose: () => setAddingCategory(false), onSaved: async () => {
                    setAddingCategory(false);
                    await load();
                } })), _jsx(ResourceSearchPalette, { open: searchOpen, resources: resources, pinnedIds: pinnedIds, onClose: () => setSearchOpen(false), onTogglePin: togglePin, onOpen: (r) => logResourceOpen(r.id), tools: [
                    // Lead CRM only when it's visible to this caller (BU-restricted).
                    ...(crmVisible
                        ? [
                            {
                                id: "crm",
                                label: "Lead Pipeline (CRM)",
                                icon: "🎯",
                                keywords: "crm leads pipeline sales deals consultant",
                                onSelect: () => {
                                    setSearchOpen(false);
                                    openCrm();
                                },
                            },
                        ]
                        : []),
                    {
                        id: "contacts",
                        label: "Key Contacts",
                        icon: "👤",
                        keywords: "contacts directory phone email partners vendors escalation",
                        onSelect: () => {
                            setSearchOpen(false);
                            openKeyContacts();
                        },
                    },
                    {
                        id: "reply-tracker",
                        label: "Reply Tracker",
                        icon: "📨",
                        keywords: "reply tracker email follow up waiting nudge sent chase response",
                        onSelect: () => {
                            setSearchOpen(false);
                            openReplyTracker();
                        },
                    },
                ], onCreateFromQuery: canManageResources
                    ? () => {
                        // Curator path — close the palette and pop the
                        // standard "Add resource" editor. The user fills out
                        // title/category/URL the same way as +Add. (Future:
                        // pre-fill the title with the query.)
                        setSearchOpen(false);
                        setNewCategoryDefault("documents");
                        setEditing("new");
                    }
                    : undefined })] }));
}
// Curator-only modal: pin a new category card. A pin is just a name +
// optional BU scope — it forces an empty category card to render so the
// curator can populate it. Categories stay free-form: the name typed
// here is matched verbatim against each resource's `category` string.
function AddCategoryModal({ units, defaultBu, existingCategories, onClose, onSaved, }) {
    const [name, setName] = useState("");
    const [businessUnitId, setBusinessUnitId] = useState(defaultBu);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    async function save() {
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Give the category a name.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.post("/resources/category-pins", {
                name: trimmed,
                business_unit_id: businessUnitId || null,
            });
            await onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't add category");
        }
        finally {
            setSaving(false);
        }
    }
    // De-dupe the suggestions list — KNOWN + custom keys can overlap.
    const suggestions = Array.from(new Set(existingCategories)).sort((a, b) => a.localeCompare(b));
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Add a category" }), _jsx("p", { className: "muted small", children: "Creates a new category card on the resources hub. The card shows up empty until someone adds resources to it." }), _jsxs("label", { children: ["Category name ", _jsx("span", { className: "muted small", children: "\u00B7 e.g. Compliance, Trainings" }), _jsx("input", { list: "add-category-suggestions", value: name, onChange: (e) => setName(e.target.value), autoFocus: true, maxLength: 40, placeholder: "Compliance" }), _jsx("datalist", { id: "add-category-suggestions", children: suggestions.map((s) => (_jsx("option", { value: s }, s))) })] }), _jsxs("label", { children: ["Business unit", _jsxs("select", { value: businessUnitId, onChange: (e) => setBusinessUnitId(e.target.value), children: [_jsx("option", { value: "", children: "Company-wide (every unit sees it)" }), units.map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] })] }), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { onClick: save, className: "btn-primary", disabled: saving, children: saving ? "Adding…" : "Add category" })] })] }) }));
}
function ResourceTile({ row, totalBuCount, showBuLabel, isPinned, onTogglePin, onEdit, onDelete, onOpen, }) {
    // When embed_html is set the tile renders the embed inline (iframe,
    // widget, etc.) — the URL becomes a small "open source" affordance
    // below the embed. Otherwise it's the standard click-tile.
    const hasEmbed = !!row.embed_html && row.embed_html.trim().length > 0;
    // Edit/Remove now live behind a quiet ⋯ menu (revealed on hover) instead
    // of always-on text links, so the cards read cleaner for editors. The menu
    // is positioned fixed (anchored to the ⋯ button) so the card's overflow
    // clipping can't hide it.
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    function openTileMenu(e) {
        if (menuOpen) {
            setMenuOpen(false);
            return;
        }
        const r = e.currentTarget.getBoundingClientRect();
        setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
        setMenuOpen(true);
    }
    // Resources are multi-BU. Three rendering paths (all gated on
    // showBuLabel — when off, no label renders at all):
    //   - Tagged with every BU in the system → "All business units"
    //     (matches the modal's "All business units" toggle).
    //   - Tagged with 1+ specific BUs → dot-separated names.
    //   - Tagged with nothing (shouldn't happen post-0029, but legacy
    //     rows or race conditions could land here) → no label.
    const names = row.business_unit_names ?? [];
    let buLabel = null;
    if (!showBuLabel) {
        buLabel = null;
    }
    else if (totalBuCount > 0 && names.length === totalBuCount) {
        buLabel = "All business units";
    }
    else if (names.length > 0) {
        buLabel = names.join(" · ");
    }
    return (_jsxs("li", { className: `resources-tile ${hasEmbed ? "resources-tile-embed" : ""}`, children: [_jsx("button", { type: "button", className: `resources-tile-pin ${isPinned ? "active" : ""}`, onClick: onTogglePin, title: isPinned ? "Unpin from Quick access" : "Pin to Quick access", "aria-pressed": isPinned, "aria-label": isPinned ? "Unpin resource" : "Pin resource", children: "\uD83D\uDCCC" }), hasEmbed ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "resources-tile-embed-head", children: [_jsx("span", { className: "resources-tile-icon", "aria-hidden": "true", children: row.icon || "🧩" }), _jsxs("div", { className: "resources-tile-body", children: [_jsx("span", { className: "resources-tile-title", children: row.title }), row.description && (_jsx("span", { className: "muted small resources-tile-desc", children: row.description })), buLabel && (_jsx("span", { className: "muted small resources-tile-bu", children: buLabel }))] })] }), _jsx("div", { className: "resources-tile-embed-body", dangerouslySetInnerHTML: {
                            __html: sanitizeEmbedHtml(row.embed_html ?? ""),
                        } }), row.url && row.url !== "embed://" && (_jsx("a", { className: "muted small resources-tile-source", href: safeHref(row.url), target: "_blank", rel: "noopener noreferrer", onClick: onOpen, children: "Open source \u2197" }))] })) : (_jsxs("a", { className: "resources-tile-link", href: safeHref(row.url), target: "_blank", rel: "noopener noreferrer", onClick: onOpen, children: [_jsx("span", { className: "resources-tile-icon", "aria-hidden": "true", children: row.icon || "🔗" }), _jsxs("span", { className: "resources-tile-body", children: [_jsx("span", { className: "resources-tile-title", children: row.title }), row.description && (_jsx("span", { className: "muted small resources-tile-desc", children: row.description })), buLabel && (_jsx("span", { className: "muted small resources-tile-bu", children: buLabel }))] }), _jsx("span", { className: "resources-tile-go", "aria-hidden": "true", children: "\u2197" })] })), row.can_edit && (_jsxs("div", { className: "resources-tile-menu-wrap", children: [_jsx("button", { type: "button", className: "resources-tile-dots", onClick: openTileMenu, "aria-haspopup": "menu", "aria-expanded": menuOpen, "aria-label": "Resource actions", title: "Edit or remove", children: "\u22EF" }), menuOpen && menuPos && (_jsxs(_Fragment, { children: [_jsx("div", { className: "resources-menu-backdrop", onClick: () => setMenuOpen(false) }), _jsxs("div", { className: "resources-dropdown", role: "menu", style: {
                                    position: "fixed",
                                    top: menuPos.top,
                                    right: menuPos.right,
                                }, children: [_jsx("button", { type: "button", role: "menuitem", onClick: () => {
                                            setMenuOpen(false);
                                            onEdit();
                                        }, children: "\u270F\uFE0F Edit" }), _jsx("button", { type: "button", role: "menuitem", className: "danger", onClick: () => {
                                            setMenuOpen(false);
                                            onDelete();
                                        }, children: "\uD83D\uDDD1 Remove" })] })] }))] }))] }));
}
function ResourceEditor({ initial, defaultCategory, units, canPickBu, onClose, onSaved, inline = false, defaultBusinessUnitIds, }) {
    const [title, setTitle] = useState(initial?.title ?? "");
    const [url, setUrl] = useState(initial?.url ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [icon, setIcon] = useState(initial?.icon ?? "");
    const [category, setCategory] = useState(initial?.category ?? defaultCategory);
    // Multi-BU model: a resource is tagged with one or more business
    // units (no more company-wide NULL). The editor uses a checkbox list
    // — admin checks every unit that should see the resource. Saving
    // requires at least one selection.
    const [businessUnitIds, setBusinessUnitIds] = useState(initial?.business_unit_ids ?? defaultBusinessUnitIds ?? []);
    function toggleBu(id) {
        setBusinessUnitIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }
    // Optional HTML embed. When non-empty the tile renders the snippet
    // inline (iframe, mini widget, status indicator) instead of being a
    // pure link. Trusted input — only admins/managers can save it.
    const [embedHtml, setEmbedHtml] = useState(initial?.embed_html ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const isEdit = !!initial;
    async function save() {
        if (!title.trim()) {
            setError("Give it a name so people can find it.");
            return;
        }
        if (!url.trim()) {
            setError("URL is required — that's what the tile links to.");
            return;
        }
        if (businessUnitIds.length === 0) {
            setError("Pick at least one business unit. Check every unit that should see this resource.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const body = {
                title: title.trim(),
                url: url.trim(),
                description: description.trim() || null,
                icon: icon.trim() || null,
                embed_html: embedHtml.trim() || null,
                category,
                business_unit_ids: businessUnitIds,
            };
            if (isEdit && initial) {
                await api.patch(`/resources/${initial.id}`, body);
            }
            else {
                await api.post("/resources", body);
            }
            await onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // Inline mode: same JSX body, different outer wrapper. We swap the
    // modal-backdrop + centered card for a plain section so the form
    // renders in place inside whatever container is hosting it.
    const wrapperClass = inline
        ? "resources-inline-editor"
        : "modal-backdrop";
    const innerClass = inline ? "resources-inline-editor-body" : "modal";
    const headingTag = inline ? "h3" : "h2";
    const Heading = headingTag;
    return (_jsx("div", { className: wrapperClass, onClick: inline ? undefined : onClose, children: _jsxs("div", { className: innerClass, onClick: (e) => e.stopPropagation(), children: [_jsx(Heading, { children: isEdit ? "Edit resource" : "Add a resource" }), _jsxs("label", { children: ["Title", _jsx("input", { value: title, onChange: (e) => setTitle(e.target.value), autoFocus: true, placeholder: "Q2 Sales Decks \u00B7 #sales-ops Teams chat \u00B7 Salesforce login" })] }), _jsxs("label", { children: ["URL", _jsx("input", { value: url, type: "url", onChange: (e) => setUrl(e.target.value), placeholder: "https://..." })] }), _jsxs("label", { children: ["Description ", _jsx("span", { className: "muted small", children: "\u00B7 optional \u00B7 admins can edit anytime" }), _jsx("textarea", { rows: 3, value: description, onChange: (e) => setDescription(e.target.value), placeholder: "What's in here / when should I open this? Multi-line descriptions are fine." })] }), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Category ", _jsx("span", { className: "muted small", children: "\u00B7 pick or type your own" }), _jsx("input", { list: "resource-categories", value: category, onChange: (e) => setCategory(e.target.value), placeholder: "Documents, Calendars, Compliance\u2026" }), _jsxs("datalist", { id: "resource-categories", children: [_jsx("option", { value: "documents", children: "Documents" }), _jsx("option", { value: "chats", children: "Team Chats" }), _jsx("option", { value: "applications", children: "Company Applications" }), _jsx("option", { value: "calendars", children: "Calendars" }), _jsx("option", { value: "contacts", children: "Key Contacts" }), _jsx("option", { value: "sops", children: "SOPs & Playbooks" }), _jsx("option", { value: "tools", children: "Company Tools" })] })] }), _jsxs("label", { children: ["Icon ", _jsx("span", { className: "muted small", children: "\u00B7 emoji optional" }), _jsx("input", { value: icon, onChange: (e) => setIcon(e.target.value), placeholder: "\uD83D\uDDC2\uFE0F" })] })] }), canPickBu && (_jsxs("div", { className: "resources-bu-picker", children: [_jsxs("div", { className: "resources-bu-picker-head", children: [_jsx("span", { children: "Business units" }), _jsx("span", { className: "muted small", children: "\u00B7 pick at least one \u00B7 check every unit that should see this" })] }), units.length === 0 ? (_jsx("p", { className: "muted small", children: "No business units exist yet \u2014 ask an admin to create one in Settings before adding resources." })) : (_jsxs(_Fragment, { children: [_jsxs("label", { className: "resources-bu-check resources-bu-check-all", children: [_jsx("input", { type: "checkbox", checked: units.length > 0 &&
                                                businessUnitIds.length === units.length, 
                                            // Mid-state indicator while only some BUs are picked
                                            // — the user can see at a glance that "All" is only
                                            // partially satisfied.
                                            ref: (el) => {
                                                if (el) {
                                                    el.indeterminate =
                                                        businessUnitIds.length > 0 &&
                                                            businessUnitIds.length < units.length;
                                                }
                                            }, onChange: (e) => {
                                                setBusinessUnitIds(e.target.checked ? units.map((u) => u.id) : []);
                                            } }), _jsxs("span", { children: [_jsx("strong", { children: "All business units" }), _jsxs("span", { className: "muted small", children: [" ", "\u00B7 check to share with every unit"] })] })] }), _jsx("div", { className: "resources-bu-checks", children: units.map((u) => (_jsxs("label", { className: "resources-bu-check", children: [_jsx("input", { type: "checkbox", checked: businessUnitIds.includes(u.id), onChange: () => toggleBu(u.id) }), _jsx("span", { children: u.name })] }, u.id))) })] }))] })), _jsxs("label", { children: ["HTML embed", " ", _jsx("span", { className: "muted small", children: "\u00B7 optional \u00B7 iframe or widget code" }), _jsx("textarea", { rows: 4, value: embedHtml, onChange: (e) => setEmbedHtml(e.target.value), placeholder: '<iframe src="https://..." width="100%" height="240"></iframe>', style: { fontFamily: "monospace", fontSize: "0.8rem" } }), _jsxs("span", { className: "muted small", children: ["Tip: most external apps need ", _jsx("code", { children: "?embed=1" }), " or similar to allow iframe embedding. If the embed is empty, the tile stays as a regular link."] })] }), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { onClick: save, className: "btn-primary", disabled: saving, children: saving ? "Saving…" : isEdit ? "Save" : "Add" })] })] }) }));
}
// Onboarding path — an ordered "first week" checklist. Everyone sees
// their path (company-wide steps + their BUs') and ticks off their own
// progress; admins/managers can add and remove steps.
function OnboardingPathSection({ canCurate, units, }) {
    const [items, setItems] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newLabel, setNewLabel] = useState("");
    const [newBu, setNewBu] = useState("");
    async function load() {
        try {
            setItems(await api.get("/resources/onboarding"));
        }
        catch {
            setItems([]);
        }
    }
    useEffect(() => {
        load();
    }, []);
    async function toggle(item) {
        setItems((xs) => xs
            ? xs.map((x) => x.id === item.id
                ? { ...x, is_completed: !x.is_completed }
                : x)
            : xs);
        try {
            await api.post(`/resources/onboarding/${item.id}/toggle`);
        }
        catch {
            // Revert on failure.
            setItems((xs) => xs
                ? xs.map((x) => x.id === item.id
                    ? { ...x, is_completed: item.is_completed }
                    : x)
                : xs);
        }
    }
    async function addStep() {
        const label = newLabel.trim();
        if (!label)
            return;
        try {
            await api.post("/resources/onboarding", {
                label,
                business_unit_id: newBu || null,
            });
            setNewLabel("");
            setNewBu("");
            setAdding(false);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't add step");
        }
    }
    async function removeStep(item) {
        if (!confirm(`Remove "${item.label}" from the onboarding path?`))
            return;
        try {
            await api.del(`/resources/onboarding/${item.id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't remove step");
        }
    }
    if (items === null)
        return null;
    // Nothing to show and the viewer can't curate — keep the page clean.
    if (items.length === 0 && !canCurate)
        return null;
    const doneCount = items.filter((i) => i.is_completed).length;
    return (_jsxs("section", { className: "resources-card onboarding-section", children: [_jsxs("header", { className: "resources-card-head", children: [_jsx("span", { className: "resources-card-icon", "aria-hidden": "true", children: "\uD83E\uDDED" }), _jsxs("div", { className: "resources-card-titleblock", children: [_jsx("h2", { children: "Onboarding path" }), _jsx("p", { className: "muted small", children: items.length > 0
                                    ? `${doneCount} of ${items.length} done`
                                    : "Your team's first-week checklist." })] }), canCurate && !adding && (_jsx("button", { type: "button", className: "btn-link", onClick: () => setAdding(true), children: "+ Add step" }))] }), items.length === 0 ? (_jsx("p", { className: "muted small resources-empty", children: "No onboarding steps yet. Add the things a new hire should do first." })) : (_jsx("ul", { className: "onboarding-list", children: items.map((i) => (_jsxs("li", { className: "onboarding-row", children: [_jsxs("label", { className: "onboarding-check", children: [_jsx("input", { type: "checkbox", checked: i.is_completed, onChange: () => toggle(i) }), _jsx("span", { className: i.is_completed ? "onboarding-done" : "", children: i.resource_url ? (_jsx("a", { href: safeHref(i.resource_url), target: "_blank", rel: "noopener noreferrer", children: i.label })) : (i.label) })] }), i.business_unit_name && (_jsx("span", { className: "muted small onboarding-bu", children: i.business_unit_name })), canCurate && i.can_edit && (_jsx("button", { type: "button", className: "btn-link onboarding-remove", onClick: () => removeStep(i), children: "Remove" }))] }, i.id))) })), adding && (_jsxs("div", { className: "onboarding-add", children: [_jsx("input", { value: newLabel, onChange: (e) => setNewLabel(e.target.value), placeholder: "e.g. Read the team handbook", autoFocus: true }), _jsxs("select", { value: newBu, onChange: (e) => setNewBu(e.target.value), children: [_jsx("option", { value: "", children: "Company-wide" }), units.map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] }), _jsx("button", { type: "button", className: "btn-primary", onClick: addStep, children: "Add" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => {
                            setAdding(false);
                            setNewLabel("");
                        }, children: "Cancel" })] }))] }));
}
// Link health — a curator-only, on-demand check. "Run check" HEADs
// every resource URL and flags 404s + links not reviewed in ~6 months.
// Healthy links are hidden; only the rows that need attention show.
function LinkHealthSection() {
    const [report, setReport] = useState(null);
    const [running, setRunning] = useState(false);
    async function run() {
        setRunning(true);
        try {
            setReport(await api.get("/resources/link-health"));
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Link check failed");
        }
        finally {
            setRunning(false);
        }
    }
    async function markReviewed(id) {
        try {
            await api.post(`/resources/${id}/mark-reviewed`);
            setReport((r) => r
                ? {
                    ...r,
                    rows: r.rows.map((row) => row.id === id
                        ? {
                            ...row,
                            stale: false,
                            last_reviewed_at: new Date().toISOString(),
                        }
                        : row),
                }
                : r);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't mark reviewed");
        }
    }
    const problems = report
        ? report.rows.filter((r) => r.status === "broken" || r.status === "unknown" || r.stale)
        : [];
    return (_jsxs("section", { className: "resources-card linkhealth-section", children: [_jsxs("header", { className: "resources-card-head", children: [_jsx("span", { className: "resources-card-icon", "aria-hidden": "true", children: "\uD83E\uDE7A" }), _jsxs("div", { className: "resources-card-titleblock", children: [_jsx("h2", { children: "Link health" }), _jsx("p", { className: "muted small", children: report
                                    ? `${report.checked} links checked · ${problems.length} need attention`
                                    : "Check resource links for 404s and stale review dates." })] }), _jsx("button", { type: "button", className: "btn-link", onClick: run, disabled: running, children: running ? "Checking…" : report ? "Re-check" : "Run check" })] }), report &&
                (problems.length === 0 ? (_jsx("p", { className: "muted small resources-empty", children: "Every link is healthy and recently reviewed." })) : (_jsx("ul", { className: "linkhealth-list", children: problems.map((row) => (_jsxs("li", { className: "linkhealth-row", children: [_jsx("span", { className: "linkhealth-title", children: row.title }), row.status === "broken" && (_jsxs("span", { className: "pill warn", children: ["broken", row.http_code ? ` · ${row.http_code}` : ""] })), row.status === "unknown" && (_jsx("span", { className: "pill", children: "unreachable" })), row.stale && _jsx("span", { className: "pill", children: "review due" }), _jsx("button", { type: "button", className: "today-schedule-btn", onClick: () => markReviewed(row.id), children: "Mark reviewed" })] }, row.id))) })))] }));
}
